import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiPlus, FiMinus, FiMaximize } = FiIcons;

const NODE_WIDTH = 240;
const NODE_HEIGHT = 100;

// Detailed 17-node FI flowchart matching the app's 11 steps
const nodes = [
  { id: 'start', title: '1. Strategic Baseline', desc: 'Define parameters & invested capital', x: 500, y: 100, color: 'slate', icon: 'FiSettings' },
  { id: 'mini_ef', title: '2. Starter EF', desc: 'Save $1,000 for immediate emergencies', x: 500, y: 250, color: 'indigo', icon: 'FiShield' },
  { id: 'match_q', title: 'Employer Match?', desc: 'Does company offer 401(k) match?', x: 500, y: 400, color: 'emerald', icon: 'FiBriefcase', isDecision: true },
  { id: 'match_a', title: '3. Max Employer Match', desc: 'Contribute exactly enough to get 100% match', x: 820, y: 400, color: 'emerald', icon: 'FiCheck' },
  { id: 'debt_q', title: 'High-Interest Debt?', desc: 'Credit cards, personal loans > 8%', x: 500, y: 550, color: 'rose', icon: 'FiTrendingDown', isDecision: true },
  { id: 'debt_a', title: '4. Debt Eradication', desc: 'Avalanche method: Highest rate first', x: 820, y: 550, color: 'rose', icon: 'FiZap' },
  { id: 'full_ef', title: '5. Full Emergency Fund', desc: '3-6 months of essential living expenses', x: 500, y: 700, color: 'indigo', icon: 'FiUmbrella' },
  { id: 'fi_target', title: '6. Calculate FI Number', desc: '25x to 28x your annual expenses', x: 500, y: 850, color: 'purple', icon: 'FiTarget' },
  { id: 'trajectory', title: '7. Strategic Alignment', desc: 'Analyze gap between current & required savings', x: 500, y: 1000, color: 'orange', icon: 'FiActivity' },
  { id: 'tax_opt', title: '8. Tax Optimization', desc: 'Fill tax-advantaged buckets in order', x: 500, y: 1150, color: 'blue', icon: 'FiLayers' },
  { id: 'hsa', title: 'Max HSA', desc: 'Triple tax-advantaged ($3,850/yr)', x: 180, y: 1300, color: 'blue', icon: 'FiHeart' },
  { id: 'ira', title: 'Max Roth IRA', desc: 'Tax-free growth ($6,500/yr)', x: 500, y: 1300, color: 'blue', icon: 'FiTrendingUp' },
  { id: '401k', title: 'Max 401(k)', desc: 'Tax-deferred growth ($22,500/yr)', x: 820, y: 1300, color: 'blue', icon: 'FiBriefcase' },
  { id: 'on_track_q', title: 'On Track for FI?', desc: 'Hitting required monthly savings?', x: 500, y: 1450, color: 'amber', icon: 'FiCompass', isDecision: true },
  { id: 'lever', title: '10. The Income Lever', desc: 'Increase income to compress timeline', x: 820, y: 1450, color: 'amber', icon: 'FiZap' },
  { id: 'bridge', title: '9. Early Retire Bridge', desc: 'Roth Ladder to access funds before 59.5', x: 500, y: 1600, color: 'teal', icon: 'FiUnlock' },
  { id: 'review', title: '11. Annual Review', desc: 'Recalibrate FI target and rebalance', x: 500, y: 1750, color: 'slate', icon: 'FiRefreshCw' },
];

const edges = [
  { from: 'start', to: 'mini_ef' },
  { from: 'mini_ef', to: 'match_q' },
  { from: 'match_q', to: 'match_a', label: 'Yes' },
  { from: 'match_q', to: 'debt_q', label: 'No' },
  { from: 'match_a', to: 'debt_q' },
  { from: 'debt_q', to: 'debt_a', label: 'Yes' },
  { from: 'debt_q', to: 'full_ef', label: 'No' },
  { from: 'debt_a', to: 'full_ef' },
  { from: 'full_ef', to: 'fi_target' },
  { from: 'fi_target', to: 'trajectory' },
  { from: 'trajectory', to: 'tax_opt' },
  { from: 'tax_opt', to: 'hsa' },
  { from: 'tax_opt', to: 'ira' },
  { from: 'tax_opt', to: '401k' },
  { from: 'hsa', to: 'on_track_q' },
  { from: 'ira', to: 'on_track_q' },
  { from: '401k', to: 'on_track_q' },
  { from: 'on_track_q', to: 'lever', label: 'No' },
  { from: 'on_track_q', to: 'bridge', label: 'Yes' },
  { from: 'lever', to: 'bridge' },
  { from: 'bridge', to: 'review' },
];

const colorMap = {
  slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-100', hover: 'hover:border-slate-300' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-100', hover: 'hover:border-indigo-300' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', hover: 'hover:border-emerald-300' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-100', hover: 'hover:border-rose-300' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100', hover: 'hover:border-purple-300' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', hover: 'hover:border-orange-300' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', hover: 'hover:border-blue-300' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100', hover: 'hover:border-amber-300' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-100', hover: 'hover:border-teal-300' },
};

const Edge = ({ from, to, label }) => {
  const fromNode = nodes.find(n => n.id === from);
  const toNode = nodes.find(n => n.id === to);
  if (!fromNode || !toNode) return null;

  const isHorizontal = fromNode.y === toNode.y;
  let startX, startY, endX, endY;

  if (isHorizontal) {
    const isRight = toNode.x > fromNode.x;
    startX = fromNode.x + (isRight ? NODE_WIDTH / 2 : -NODE_WIDTH / 2);
    startY = fromNode.y;
    endX = toNode.x + (isRight ? -NODE_WIDTH / 2 : NODE_WIDTH / 2);
    endY = toNode.y;
  } else {
    startX = fromNode.x;
    startY = fromNode.y + NODE_HEIGHT / 2;
    endX = toNode.x;
    endY = toNode.y - NODE_HEIGHT / 2;
  }

  let pathD;
  if (startX === endX || startY === endY) {
    pathD = `M ${startX} ${startY} L ${endX} ${endY}`;
  } else {
    pathD = `M ${startX} ${startY} C ${startX} ${(startY + endY) / 2}, ${endX} ${(startY + endY) / 2}, ${endX} ${endY}`;
  }

  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <g>
      <path 
        d={pathD} 
        fill="none" 
        stroke="#cbd5e1" 
        strokeWidth="3" 
        markerEnd="url(#arrowhead)" 
        strokeDasharray={label === 'No' ? '6 6' : 'none'} 
      />
      {label && (
        <g transform={`translate(${midX}, ${midY})`}>
          <rect x="-18" y="-12" width="36" height="24" fill="white" rx="6" stroke="#e2e8f0" strokeWidth="1" />
          <text x="0" y="4" fill="#64748b" fontSize="11" fontWeight="900" textAnchor="middle">{label}</text>
        </g>
      )}
    </g>
  );
};

const Node = ({ node }) => {
  const colors = colorMap[node.color] || colorMap.slate;
  
  return (
    <div
      className={`absolute bg-white border-2 ${colors.border} ${colors.hover} shadow-xl p-4 flex flex-col items-center justify-center text-center transition-all z-10`}
      style={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        left: node.x - NODE_WIDTH / 2,
        top: node.y - NODE_HEIGHT / 2,
        borderRadius: node.isDecision ? '9999px' : '1.5rem'
      }}
    >
      <div className={`w-8 h-8 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center mb-1.5`}>
        <SafeIcon icon={node.icon} className="text-sm" />
      </div>
      <h3 className="text-[11px] font-black text-slate-900 leading-tight mb-1 uppercase tracking-widest">{node.title}</h3>
      <p className="text-[9px] font-bold text-slate-500 leading-tight">{node.desc}</p>
    </div>
  );
};

const RoadmapDiagramModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 md:p-8 bg-slate-900/80 backdrop-blur-md">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-7xl h-[90vh] rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-slate-100 z-20 bg-white">
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Financial Roadmap Diagram</h2>
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mt-1">Interactive Flowchart</p>
            </div>
            <button 
              onClick={onClose} 
              className="w-10 h-10 bg-slate-50 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full flex items-center justify-center transition-all"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 relative bg-slate-50/50 overflow-hidden">
            <TransformWrapper
              initialScale={0.8}
              minScale={0.2}
              maxScale={2}
              centerOnInit={true}
              wheel={{ step: 0.1 }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <React.Fragment>
                  {/* Floating Controls */}
                  <div className="absolute bottom-6 right-6 z-50 flex gap-2 bg-white p-2 rounded-2xl shadow-xl border border-slate-100">
                    <button onClick={() => zoomIn()} className="w-10 h-10 bg-slate-50 text-slate-600 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"><SafeIcon icon={FiPlus} /></button>
                    <button onClick={() => zoomOut()} className="w-10 h-10 bg-slate-50 text-slate-600 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"><SafeIcon icon={FiMinus} /></button>
                    <button onClick={() => resetTransform()} className="w-10 h-10 bg-slate-50 text-slate-600 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"><SafeIcon icon={FiMaximize} /></button>
                  </div>
                  
                  {/* Zoomable Canvas */}
                  <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
                    <div style={{ width: 1000, height: 1900, position: 'relative' }} className="cursor-grab active:cursor-grabbing">
                      
                      {/* SVG Background for Edges */}
                      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                        <defs>
                          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                            <polygon points="0 0, 10 3.5, 0 7" fill="#cbd5e1" />
                          </marker>
                        </defs>
                        {edges.map((edge, idx) => (
                          <Edge key={idx} from={edge.from} to={edge.to} label={edge.label} />
                        ))}
                      </svg>

                      {/* HTML Nodes */}
                      {nodes.map(node => (
                        <Node key={node.id} node={node} />
                      ))}
                      
                    </div>
                  </TransformComponent>
                </React.Fragment>
              )}
            </TransformWrapper>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default RoadmapDiagramModal;